import React, { useState, useRef, useEffect } from 'react';
import api from '../../services/api';

// Fetches and displays current stock for a linked item
const StockBadge = ({ itemId }) => {
    const [stock, setStock] = useState(null);

    useEffect(() => {
        if (!itemId) return;
        api.get(`/items/${itemId}`)
            .then(res => setStock({ qty: res.data.quantity, unit: res.data.unitType }))
            .catch(() => setStock(null));
    }, [itemId]);

    if (!itemId || stock === null) return null;

    const qty = parseFloat(stock.qty) || 0;
    const low = qty < 10;

    return (
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            low ? 'bg-red-50 text-red-500' : 'bg-gray-100 text-gray-500'
        }`}>
            {low ? '⚠ ' : ''}In stock: {qty} {stock.unit || ''}
        </span>
    );
};

/**
 * MaterialRow
 *
 * Props:
 *   entry    — { itemId, itemName, quantityUsed, unit, _key, readOnly? }
 *   index    — row index
 *   onChange — (index, updatedEntry) => void
 *   onRemove — (index) => void
 *   locked   — bool — disables all inputs (after push to production)
 *
 * readOnly on entry:
 *   When true the item name field is a plain label, not an editable input.
 *   Used for pre-populated rows (grit, bond) whose names come from the grade inputs.
 */
const MaterialRow = ({ entry, index, onChange, onRemove, locked }) => {
    const [query, setQuery]       = useState(entry.itemName || '');
    const [results, setResults]   = useState([]);
    const [loading, setLoading]   = useState(false);
    const [showDrop, setShowDrop] = useState(false);
    const dropRef                 = useRef(null);

    // Sync display query when parent updates itemName (e.g. on re-generate)
    useEffect(() => { setQuery(entry.itemName || ''); }, [entry.itemName]);

    // Close dropdown on outside click
    useEffect(() => {
        const handler = (e) => {
            if (dropRef.current && !dropRef.current.contains(e.target)) setShowDrop(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleSearch = async () => {
        if (!query.trim()) return;
        setLoading(true);
        try {
            const res = await api.get(`/items/search?query=${encodeURIComponent(query)}&field=name&limit=8`);
            setResults(res.data.items || []);
            setShowDrop(true);
        } catch {
            setResults([]);
            setShowDrop(true);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); handleSearch(); }
    };

    const handleSelect = (item) => {
        onChange(index, {
            ...entry,
            itemId:   item._id,
            itemName: item.name,
            unit:     item.unitType || entry.unit,
        });
        setQuery(item.name);
        setShowDrop(false);
    };

    const handleNameChange = (val) => {
        setQuery(val);
        onChange(index, { ...entry, itemName: val, itemId: null });
    };

    const isReadOnly = !!entry.readOnly;
    const isDisabled = locked || isReadOnly;

    const inputBase = `w-full px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400`;
    const inputState = isDisabled
        ? 'bg-gray-50 text-gray-600 border-gray-200 cursor-default'
        : entry.itemId
            ? 'border-green-300 bg-green-50'
            : 'border-gray-300';

    return (
        <div className={`grid grid-cols-12 gap-2 items-start py-2 px-3 rounded-lg mb-1 ${locked ? 'bg-gray-50' : 'bg-white border border-gray-100'}`}>

            {/* ── Item name ──────────────────────────────────────────── */}
            <div className="col-span-5 relative" ref={dropRef}>
                <div className="flex gap-1">
                    {isReadOnly ? (
                        // Pre-filled rows (grit, bond) show name as a read-only badge
                        <div className={`flex-1 px-2 py-1.5 border rounded text-sm bg-gray-50 text-gray-700 border-gray-200 font-medium truncate`}
                            title={entry.itemName}>
                            {entry.itemName}
                        </div>
                    ) : (
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => handleNameChange(e.target.value)}
                            onKeyDown={handleKeyDown}
                            disabled={locked}
                            placeholder="Item name…"
                            className={`flex-1 min-w-0 ${inputBase} ${inputState}`}
                        />
                    )}

                    {/* Search button — shown when not locked and not read-only */}
                    {!locked && (
                        <button
                            type="button"
                            onClick={handleSearch}
                            disabled={loading || !query.trim()}
                            title="Search inventory"
                            className="px-2 py-1 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 text-white rounded text-xs transition-colors flex-shrink-0"
                        >
                            {loading ? '…' : '🔍'}
                        </button>
                    )}
                </div>

                <div className="block">
                {/* Stock level for linked items */}
                <StockBadge itemId={entry.itemId} />
                {/* Linked indicator */}
                {entry.itemId && !locked && (
                    <span className="text-xs text-green-600 mt-0.5 inline-block">✓ linked</span>
                )}
                                {/* Unlinked warning */}
                                {parseFloat(entry.quantityUsed) > 0 && !entry.itemId && (
                                    <span className="text-xs text-red-500">
                                        ⚠ Not linked — search and select an item
                                    </span>
                                )}
                </div>

                {/* Search dropdown */}
                {showDrop && (
                    <ul className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto text-sm">
                        {results.map(item => (
                            <li
                                key={item._id}
                                onClick={() => handleSelect(item)}
                                className="px-3 py-1.5 hover:bg-blue-50 cursor-pointer flex justify-between"
                            >
                                <span>{item.name}</span>
                                <span className="text-gray-400 text-xs ml-2">{item.quantity} {item.unitType}</span>
                            </li>
                        ))}
                        {results.length === 0 && (
                            <li className="px-3 py-2 text-gray-400 text-xs text-center">No items found</li>
                        )}
                    </ul>
                )}
            </div>

            {/* ── Quantity ───────────────────────────────────────────── */}
            <div className="col-span-4">
                <input
                    type="number"
                    value={entry.quantityUsed}
                    onChange={(e) => onChange(index, { ...entry, quantityUsed: e.target.value })}
                    disabled={locked}
                    placeholder="Qty"
                    step="0.001"
                    className={`w-full px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        locked ? 'bg-gray-50 text-gray-500 border-gray-200' : 'border-gray-300'
                    }`}
                />
            </div>

            {/* ── Unit ──────────────────────────────────────────────── */}
            <div className="col-span-2">
                <input
                    type="text"
                    value={entry.unit}
                    onChange={(e) => onChange(index, { ...entry, unit: e.target.value })}
                    disabled={locked}
                    placeholder="Unit"
                    className={`w-full px-2 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-blue-400 ${
                        locked ? 'bg-gray-50 text-gray-500 border-gray-200' : 'border-gray-300'
                    }`}
                />
            </div>

            {/* ── Remove ────────────────────────────────────────────── */}
            <div className="col-span-1 flex justify-center pt-1">
                {!locked && !isReadOnly && (
                    <button
                        type="button"
                        onClick={() => onRemove(index)}
                        className="text-gray-300 hover:text-red-500 text-xl leading-none transition-colors"
                        title="Remove row"
                    >
                        ×
                    </button>
                )}
            </div>
        </div>
    );
};

export default MaterialRow;